import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, withKeys as _withKeys, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, createVNode as _createVNode, Teleport as _Teleport, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = ["id", "aria-labelledby"]

import { ref, watch } from 'vue';
import { useClickOutside } from '@shell/composables/useClickOutside';
import { focusedFromKeyboard, useDropdownContext } from '@components/RcDropdown/useDropdownContext';
import { useSubmenuPlacement } from '@components/RcDropdown/useSubmenuPlacement';

import type { Placement } from 'floating-vue';

type ReferenceNode = () => Element | undefined | null;


export default /*@__PURE__*/_defineComponent({
  __name: 'RcDropdown',
  props: {
    ariaLabel: {},
    distance: {},
    referenceNode: {},
    open: { type: Boolean, default: false },
    placement: { default: 'bottom-end' },
    container: {}
  },
  emits: ['update:open'],
  setup(__props: any, { emit: __emit }) {

/**
 * Offers a list of choices to the user, such as a set of actions or functions.
 * Opened by activating RcDropdownTrigger.
 *
 * Example:
 *
 *  <rc-dropdown :aria-label="t('nav.actionMenu.label')">
 *    <rc-dropdown-trigger tertiary>
 *      <i class="icon icon-actions" />
 *    </rc-dropdown-trigger>
 *    <template #dropdownCollection>
 *      <rc-dropdown-item @click="performAction()">
 *        Action 1
 *      </rc-dropdown-item>
 *      <rc-dropdown-separator />
 *      <rc-dropdown-item @click="performAction()">
 *        Action 2
 *      </rc-dropdown-item>
 *    </template>
 *  </rc-dropdown>
 */
const props = __props;

const emit = __emit;

const {
  isMenuOpen,
  showMenu,
  returnFocus,
  setFocus,
  provideDropdownContext,
  registerDropdownCollection,
  handleKeydown,
  setDropdownDimensions,
  submenus,
  SubmenuContent,
} = useDropdownContext(emit);

const {
  submenuId,
  submenus: registeredSubmenus,
  activeSubmenu,
  shownSubmenu,
  submenuTarget,
  cancelSubmenuSwitch,
  refreshSubmenuItems,
  onSubmenuShown,
  onSubmenuKeydown,
} = submenus;

provideDropdownContext();

const popperContainer = ref<HTMLElement | null>(null);
const dropdownTarget = ref<HTMLElement | null>(null);

watch(() => props.open, (open) => {
  if (open === isMenuOpen.value) {
    return;
  }

  if (!open) {
    // The focus would otherwise stay in the hidden popper
    if (popperContainer.value?.contains(document.activeElement)) {
      returnFocus();
    } else {
      showMenu(false);
    }

    return;
  }

  if (focusedFromKeyboard()) {
    handleKeydown();
  }

  showMenu(true);
}, { immediate: true });

// A submenu is drawn beside the menu, but a click in it is still inside
useClickOutside(dropdownTarget, () => showMenu(false), { ignore: ['.rc-dropdown-submenu'] });

/** A submenu's key presses bubble up here too; only the menu they're in answers them */
const ownsEvent = (e: Event) => {
  const target = e.target as HTMLElement | null;

  return !!dropdownTarget.value && target?.closest?.('[dropdown-menu-collection]') === dropdownTarget.value;
};

const onKeydown = (e: KeyboardEvent) => {
  if (ownsEvent(e)) {
    handleKeydown();
  }
};

const onArrow = (e: KeyboardEvent, direction: 'down' | 'up') => {
  if (!ownsEvent(e)) {
    return;
  }

  e.preventDefault();
  setFocus(direction);
};

const onTab = (e: KeyboardEvent) => {
  if (ownsEvent(e)) {
    showMenu(false);
  }
};

const onEscape = (e: KeyboardEvent) => {
  if (ownsEvent(e)) {
    returnFocus();
  }
};

/** The width of the menu's border, which a submenu overlaps so the two share one edge */
const menuBorder = ref(0);

/** The menu's popper, border included, so a submenu lines up with its top */
const menuBox = () => dropdownTarget.value?.closest('.v-popper__popper') || dropdownTarget.value;

// The submenus' popper is beside the menu's, not in it, which scrolls once cut to the screen, and
// never flips over the menu, which would cover the items the pointer came from
const { submenuLift, start: placeSubmenu, stop: stopPlacingSubmenu } = useSubmenuPlacement(menuBox, shownSubmenu, submenuTarget);

const onSubmenuApplyShow = () => {
  placeSubmenu();
  onSubmenuShown();
};

const applyShow = () => {
  const box = menuBox();

  menuBorder.value = box ? parseFloat(getComputedStyle(box).borderTopWidth) || 0 : 0;
  setDropdownDimensions(dropdownTarget.value);
  registerDropdownCollection(dropdownTarget.value);
  setFocus('down');
};


return (_ctx: any,_cache: any) => {
  const _component_v_dropdown = _resolveComponent("v-dropdown")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_dropdown, {
      "no-auto-focus": "",
      triggers: [],
      shown: _unref(isMenuOpen),
      "auto-hide": false,
      container: popperContainer.value,
      placement: __props.placement,
      distance: __props.distance,
      "reference-node": __props.referenceNode,
      onApplyShow: applyShow
    }, {
      popper: _withCtx(() => [
        _createElementVNode("div", {
          ref_key: "dropdownTarget",
          ref: dropdownTarget,
          class: "dropdownTarget",
          tabindex: "-1",
          role: "menu",
          "aria-orientation": "vertical",
          "dropdown-menu-collection": "",
          "aria-label": __props.ariaLabel || 'Dropdown Menu',
          onKeydown: [
            onKeydown,
            _cache[0] || (_cache[0] = _withKeys(($event: any) => (onArrow($event, 'down')), ["down"])),
            _cache[1] || (_cache[1] = _withKeys(($event: any) => (onArrow($event, 'up')), ["up"]))
          ]
        }, [
          _renderSlot(_ctx.$slots, "dropdownCollection")
        ], 40, _hoisted_1),
        _cache[5] || (_cache[5] = _createTextVNode()),
        (_unref(registeredSubmenus).length)
          ? (_openBlock(), _createBlock(_component_v_dropdown, {
              key: 0,
              "no-auto-focus": "",
              triggers: [],
              shown: !!_unref(activeSubmenu),
              "auto-hide": false,
              container: popperContainer.value,
              placement: _unref(shownSubmenu)?.side === 'left' ? 'left-start' : 'right-start',
              distance: -menuBorder.value,
              flip: false,
              shift: false,
              skidding: -_unref(submenuLift),
              "popper-class": "rc-dropdown-submenu",
              "reference-node": menuBox,
              onApplyShow: onSubmenuApplyShow,
              onApplyHide: _unref(stopPlacingSubmenu)
            }, {
              popper: _withCtx(() => [
                _createElementVNode("div", {
                  id: _unref(submenuId),
                  ref_key: "submenuTarget",
                  ref: submenuTarget,
                  class: "dropdownTarget",
                  tabindex: "-1",
                  role: "menu",
                  "aria-orientation": "vertical",
                  "dropdown-menu-collection": "",
                  "aria-labelledby": _unref(shownSubmenu)?.labelId,
                  onKeydownCapture: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(refreshSubmenuItems) && _unref(refreshSubmenuItems)(...args))),
                  onKeydown: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(onSubmenuKeydown) && _unref(onSubmenuKeydown)(...args))),
                  onMouseenter: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(cancelSubmenuSwitch) && _unref(cancelSubmenuSwitch)(...args)))
                }, [
                  (_unref(shownSubmenu))
                    ? (_openBlock(), _createBlock(_unref(SubmenuContent), {
                        key: _unref(shownSubmenu).labelId,
                        submenu: _unref(shownSubmenu)
                      }, null, 8, ["submenu"]))
                    : _createCommentVNode("", true)
                ], 40, _hoisted_2)
              ]),
              _: 1
            }, 8, ["shown", "container", "placement", "distance", "skidding", "onApplyHide"]))
          : _createCommentVNode("", true)
      ]),
      default: _withCtx(() => [
        _renderSlot(_ctx.$slots, "default"),
        _cache[6] || (_cache[6] = _createTextVNode())
      ]),
      _: 3
    }, 8, ["shown", "container", "placement", "distance", "reference-node"]),
    _cache[7] || (_cache[7] = _createTextVNode()),
    (_openBlock(), _createBlock(_Teleport, {
      to: __props.container || 'body',
      disabled: !__props.container
    }, [
      _createElementVNode("div", {
        ref_key: "popperContainer",
        ref: popperContainer,
        class: "popperContainer",
        onKeydown: [
          _withKeys(onTab, ["tab"]),
          _withKeys(onEscape, ["escape"])
        ]
      }, null, 544)
    ], 8, ["to", "disabled"]))
  ], 64))
}
}

})